import { Task, TaskStatus, TaskPriority } from '../types';
import fs from 'fs-extra';
import path from 'path';
import { format } from 'date-fns';
import { zhCN } from 'date-fns/locale';

export interface TodoExportOptions {
  projectName?: string;
  includeStats?: boolean;
  includeTimeline?: boolean;
  locale?: 'zh' | 'en';
}

export class TodoExporter {
  private options: Required<TodoExportOptions>;

  constructor(options: TodoExportOptions = {}) {
    this.options = {
      projectName: 'Project Tasks',
      includeStats: true,
      includeTimeline: true,
      locale: 'zh',
      ...options
    };
  }

  async export(tasks: Task[], outputPath: string): Promise<void> {
    const content = this.generateContent(tasks);
    await fs.ensureDir(path.dirname(outputPath));
    await fs.writeFile(outputPath, content, 'utf-8');
  }

  generateContent(tasks: Task[]): string {
    const lines: string[] = [];
    const now = new Date();
    const isZh = this.options.locale === 'zh';

    // Header
    lines.push(`# ${this.options.projectName} - ${isZh ? '任务清单' : 'Task List'}`);
    lines.push('');
    lines.push(`> ${isZh ? '最后更新' : 'Last Updated'}: ${this.formatDate(now)}`);
    lines.push('');

    // Statistics
    if (this.options.includeStats) {
      lines.push(`## 📊 ${isZh ? '任务统计' : 'Task Statistics'}`);
      lines.push('');
      
      const stats = this.calculateStats(tasks);
      lines.push(`- ${isZh ? '总任务数' : 'Total Tasks'}: **${stats.total}**`);
      lines.push(`- ${isZh ? '已完成' : 'Completed'}: **${stats.completed}** (${stats.completionRate}%)`);
      lines.push(`- ${isZh ? '进行中' : 'In Progress'}: **${stats.inProgress}**`);
      lines.push(`- ${isZh ? '待处理' : 'Pending'}: **${stats.pending}**`);
      lines.push('');
      
      // Progress bar
      const progressBar = this.generateProgressBar(stats.completionRate);
      lines.push(`${isZh ? '进度' : 'Progress'}: ${progressBar} ${stats.completionRate}%`);
      lines.push('');
    }

    // Tasks by Status
    const tasksByStatus = this.groupTasksByStatus(tasks);
    
    // In Progress Tasks
    if (tasksByStatus.in_progress.length > 0) {
      lines.push(`## 🚀 ${isZh ? '进行中' : 'In Progress'}`);
      lines.push('');
      this.addTaskList(lines, tasksByStatus.in_progress);
      lines.push('');
    }

    // Pending Tasks by Priority
    if (tasksByStatus.pending.length > 0) {
      lines.push(`## 📋 ${isZh ? '待处理' : 'Pending'}`);
      lines.push('');
      
      const pendingByPriority = this.groupTasksByPriority(tasksByStatus.pending);
      
      if (pendingByPriority.high.length > 0) {
        lines.push(`### 🔴 ${isZh ? '高优先级' : 'High Priority'}`);
        lines.push('');
        this.addTaskList(lines, pendingByPriority.high);
        lines.push('');
      }
      
      if (pendingByPriority.medium.length > 0) {
        lines.push(`### 🟡 ${isZh ? '中优先级' : 'Medium Priority'}`);
        lines.push('');
        this.addTaskList(lines, pendingByPriority.medium);
        lines.push('');
      }
      
      if (pendingByPriority.low.length > 0) {
        lines.push(`### 🟢 ${isZh ? '低优先级' : 'Low Priority'}`);
        lines.push('');
        this.addTaskList(lines, pendingByPriority.low);
        lines.push('');
      }
    }

    // Completed Tasks
    if (tasksByStatus.completed.length > 0) {
      lines.push(`## ✅ ${isZh ? '已完成' : 'Completed'}`);
      lines.push('');
      
      // Only show recent completed tasks (last 7 days)
      const recentCompleted = tasksByStatus.completed.filter(task => {
        if (!task.completedAt) return false;
        const daysDiff = (now.getTime() - task.completedAt.getTime()) / (1000 * 60 * 60 * 24);
        return daysDiff <= 7;
      });
      
      if (recentCompleted.length > 0) {
        lines.push(`*${isZh ? '最近7天完成的任务' : 'Completed in the last 7 days'}*`);
        lines.push('');
        this.addTaskList(lines, recentCompleted, true);
        lines.push('');
      }
      
      lines.push(`*${isZh ? '共' : 'Total'} ${tasksByStatus.completed.length} ${isZh ? '个已完成任务' : 'completed tasks'}*`);
      lines.push('');
    }

    // Timeline
    if (this.options.includeTimeline && tasks.length > 0) {
      lines.push(`## 📅 ${isZh ? '时间线' : 'Timeline'}`);
      lines.push('');
      
      const recentTasks = this.getRecentTasks(tasks, 5);
      recentTasks.forEach(task => {
        const date = task.updatedAt || task.createdAt || now;
        const dateStr = this.formatDate(date);
        const action = task.status === 'completed' ? (isZh ? '完成' : 'Completed') : 
                       task.status === 'in_progress' ? (isZh ? '开始' : 'Started') : 
                       (isZh ? '创建' : 'Created');
        lines.push(`- **${dateStr}** - ${action}: ${task.content}`);
      });
      lines.push('');
    }

    // Footer
    lines.push('---');
    lines.push('');
    lines.push(`*${isZh ? '此文件由 Workflow CLI 自动生成，请勿手动编辑' : 'This file is auto-generated by Workflow CLI, do not edit manually'}*`);
    lines.push('');

    return lines.join('\n');
  }

  private addTaskList(lines: string[], tasks: Task[], isCompleted: boolean = false): void {
    tasks.forEach(task => {
      const checkbox = isCompleted ? '[x]' : '[ ]';
      const tags = task.tags && task.tags.length > 0 ? ` \`${task.tags.join('` `')}\`` : '';
      const due = task.dueDate ? ` 📅 ${this.formatDate(task.dueDate)}` : '';
      const owner = task.owner ? ` @${task.owner}` : '';
      
      lines.push(`- ${checkbox} ${task.content}${tags}${due}${owner}`);
      
      if (task.description) {
        lines.push(`  > ${task.description}`);
      }
    });
  }

  private calculateStats(tasks: Task[]) {
    const total = tasks.length;
    const completed = tasks.filter(t => t.status === 'completed').length;
    const inProgress = tasks.filter(t => t.status === 'in_progress').length;
    const pending = tasks.filter(t => t.status === 'pending').length;
    const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;

    return { total, completed, inProgress, pending, completionRate };
  }

  private generateProgressBar(percentage: number): string {
    const filled = Math.round(percentage / 5);
    const empty = 20 - filled;
    
    if (filled === 0) {
      return `[${this.options.locale === 'zh' ? '░'.repeat(20) : ' '.repeat(20)}]`;
    }
    
    const fillChar = this.options.locale === 'zh' ? '█' : '=';
    const emptyChar = this.options.locale === 'zh' ? '░' : ' ';
    return `[${fillChar.repeat(filled)}${emptyChar.repeat(empty)}]`;
  }

  private groupTasksByStatus(tasks: Task[]) {
    return {
      pending: tasks.filter(t => t.status === 'pending'),
      in_progress: tasks.filter(t => t.status === 'in_progress'),
      completed: tasks.filter(t => t.status === 'completed')
    };
  }

  private groupTasksByPriority(tasks: Task[]) {
    return {
      high: tasks.filter(t => t.priority === 'high'),
      medium: tasks.filter(t => t.priority === 'medium'),
      low: tasks.filter(t => t.priority === 'low')
    };
  }

  private getRecentTasks(tasks: Task[], limit: number): Task[] {
    return tasks
      .filter(task => task.updatedAt || task.createdAt)
      .sort((a, b) => {
        const dateA = (a.updatedAt || a.createdAt || new Date()).getTime();
        const dateB = (b.updatedAt || b.createdAt || new Date()).getTime();
        return dateB - dateA;
      })
      .slice(0, limit);
  }

  private formatDate(date: Date): string {
    const locale = this.options.locale === 'zh' ? zhCN : undefined;
    const formatStr = this.options.locale === 'zh' ? 'yyyy年MM月dd日 HH:mm' : 'yyyy-MM-dd HH:mm';
    return format(date, formatStr, { locale });
  }
}